import { polyLine2MinXEdgeIndex } from "building-common/profile2Utils";
import { wallStructuralAssemblyName } from "building-common/walls/wallStructuralAssembly";
import type { BuildingEntityHandle } from "building-types/entities/buildingEntityHandle";
import type { DocEntityHandle, LevelHandle, RoomHandle } from "building-types/handles/docEntityHandleTypes";
import type { GeneratedRoofSystem } from "building-types/roofs/generatedRoofSystemTypes";
import { RoomCondition, RoomEnclosure } from "building-types/rooms/roomTypesEnum";
import type { WallCatalogSettings } from "building-types/settings/wallCatalogSettings";
import type { WallAssemblyStructuralLayer } from "building-types/walls/wallAssemblyTypes";
import { structuralMaterialHash } from "building-types/walls/wallCatalogQuery";
import { WallType } from "building-types/walls/wallEnums";
import { WallLayerType } from "building-types/walls/wallEnums";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { WallCondition } from "building-types/walls/wallType";
import { WallLOD, type WallMass } from "building-types/walls/wallTypes";
import type { Table } from "common/data/table";
import { wallAssemblyLayerQuery } from "doc/elements/walls/wallQueries";
import { roomIsVirtualRoofRoom } from "doc/rooms/roomQuery";
import type { AABB2 } from "geom/aabb2Types";
import { angleClean } from "geom/angle";
import { faceReverse, faceCopy, faceNew, faceProjectOnPlane, type Face, faceArea } from "geom/brep";
import { ZERO_EPSILON } from "geom/core";
import { type Line2Segment, line2Vec, line2Len, line2Eval } from "geom/line2";
import { offsetPolyLine2SSkFallback } from "geom/offset";
import { planeNormal, planeDistanceTo, planeByThreePoints, planeProjectPolyLine3, planeCopyReversed } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { PolygonRoundingMode, polyOp } from "geom/polyclip/polyClip";
import { trimFaceWithFaces } from "geom/polyclip/trimFaceWithFaces";
import { polyLine2IsRectangle, polyLine2Edge, polyLine2AABB2, polyLine2SetCW } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine3New } from "geom/polyLine3";
import { ray2New } from "geom/ray2";
import type { Ray2 } from "geom/ray2Types";
import { splitPolyLine2ByRay2, splitPolyLine2ByRay2s } from "geom/split";
import { unionCoplanarFaces } from "geom/unionFaces";
import { vec2AngleBetween } from "geom/vec2";
import { vec3AngleBetween } from "geom/vec3";
import { getWallPath } from "../elements/walls/wall";
import { wallMassLayers } from "../elements/walls/wallLayers";

const SLOPE_EPSILON = 0.0001;

export interface WallStructuralDomainSettings {
  breakAtBottom?: boolean;
  breakAtTop?: boolean;
  breakAtXYDomain?: boolean;
  breakAtGarageCondition?: boolean;
  trimWithRoofBottomFaces?: boolean;
  trimWithRoofTopFaces?: boolean;
  trimFaceOffset?: number;
  splitNonRectangularDomains?: boolean;
}

/**
 * Do not trim faces that are above the level index.
 */
interface TrimFacesByLevelIndex {
  [levelIndex: number]: Face[];
}

interface TrimFaces {
  original: TrimFacesByLevelIndex;
  offset: TrimFacesByLevelIndex;
}

export interface DomainCandidates {
  [hash: string]: DomainCandidate[];
}

export interface DomainCandidatesByPlane {
  [hash: string]: {
    /**
     * All faces in this domain should be reprojected to this plane.
     */
    readonly plane: Plane;
    readonly candidates: DomainCandidate[];
  };
}

interface DomainCandidate {
  readonly domain: PolyLine2;
  readonly plane: Plane;
  readonly aabb2: AABB2;
  readonly levelIndex: number;
  readonly generatingLevels: Set<LevelHandle>;
  readonly layers: { [hash: string]: WallAssemblyStructuralLayer };
  readonly condition: WallCondition;
  readonly leftRoomCondition: RoomCondition;
  readonly rightRoomCondition: RoomCondition;
  readonly leftRooms: Set<RoomHandle>;
  readonly rightRooms: Set<RoomHandle>;
  readonly parentHandles: { [id: string]: BuildingEntityHandle | DocEntityHandle };
}

export interface LevelIndexByLevelId {
  [levelId: string]: number;
}

/**
 * Gather candidate wall domains from wall systems (some of which
 * are located in roof systems).
 */
export function candidatesByPlane(
  walls: Table<WallSystem>,
  roofSystems: Table<GeneratedRoofSystem>,
  levelIndexById: LevelIndexByLevelId,
  wallCatalog: WallCatalogSettings,
  maxLevelIndex: number,
  settings: WallStructuralDomainSettings,
): DomainCandidatesByPlane {
  /**
   * Establish faces that should be used to trim wall domains (currently
   * only roof planes).
   */
  const trimFaces = getTrimFacesFromRoofSystems(roofSystems, levelIndexById, settings);

  const byPlane: DomainCandidatesByPlane = {};
  for (const wallSystemId in walls) {
    const wallSystem: WallSystem = walls[wallSystemId];

    // gather generating levels from all rooms in the wall system
    const generatingLevels = Object.values(wallSystem.walls)
      .map((w) => {
        const path = getWallPath(w);
        return [path.leftRoom?.level.handle, path.rightRoom?.level.handle];
      })
      .flat()
      .reduce((acc, levelHandle) => {
        if (levelHandle) {
          acc.add(levelHandle);
        }
        return acc;
      }, new Set<LevelHandle>());

    const handles: { [id: string]: BuildingEntityHandle | DocEntityHandle } = {};
    if (wallSystem.docEntityHandle) {
      handles[wallSystem.id] = wallSystem.docEntityHandle;
    }

    addCandidatesByPlaneFromWallSystem(
      settings,
      wallCatalog,
      wallSystem,
      levelIndexById,
      byPlane,
      trimFaces,
      maxLevelIndex,
      generatingLevels,
      handles,
    );
  }

  // Note(Andrew): Roof systems are not currently supported.

  // for (const roofSystemId in roofSystems) {
  //   const roofSystem = roofSystems[roofSystemId];
  //   for (const roofId in roofSystem.roofs) {
  //     const roof = roofSystem.roofs[roofId];
  //     if (!roof.roofWallSystem) {
  //       continue;
  //     }

  //     const levelIndex = levelIndexById[roof.levelId];

  //     const handles: {[id: string]: BuildingEntityHandle | DocEntityHandle} = {};
  //     if (roofSystem.docEntityHandle && roofSystem.docEntityHandle.hasOwnProperty("id")) {
  //       handles[roofSystem.docEntityHandle["id"]] = roofSystem.docEntityHandle;
  //     }
  //     if (roof.docEntityHandle && roof.docEntityHandle.hasOwnProperty("id")) {
  //       handles[roof.docEntityHandle["id"]] = roof.docEntityHandle;
  //     }

  //     addCandidatesByPlaneFromWallSystem(
  //       wallCatalog,
  //       roof.roofWallSystem.wallSystem,
  //       levelIndexById,
  //       byPlane,
  //       trimFaces,
  //       levelIndex,
  //       new Set(), // roof-sourced wall domains do not get generating levels
  //       handles,
  //     );
  //   }
  // }

  return byPlane;
}

export function candidatesByPlaneBottomElevAssemblyCondition(
  settings: WallStructuralDomainSettings,
  byPlane: DomainCandidatesByPlane,
  wallCatalog: WallCatalogSettings,
): DomainCandidates {
  const processed: DomainCandidates = {};
  for (const planeHash in byPlane) {
    // group candidates by domain, material, wall condition, and adjacent room conditions
    const byDomainAssemblyCondition: DomainCandidates = {};
    const { candidates } = byPlane[planeHash];
    for (const candidate of candidates) {
      const { domain, layers, condition, leftRoomCondition, rightRoomCondition } = candidate;
      const assemblyHash = wallStructuralAssemblyName(Object.values(layers), wallCatalog);

      if (assemblyHash === "") {
        continue;
      }

      const aabb2 = polyLine2AABB2(domain);
      const domainHash = `${Math.round(aabb2.minY)}:${Math.round(aabb2.maxY)}`;

      const conditionHash = hashWallCondition(condition, settings);
      const roomConditionPairHash = hashRoomConditionPair(leftRoomCondition, rightRoomCondition);

      const hash = `${domainHash}_${assemblyHash}_${conditionHash}_${roomConditionPairHash}`;
      if (!byDomainAssemblyCondition[hash]) {
        byDomainAssemblyCondition[hash] = [];
      }

      byDomainAssemblyCondition[hash].push(candidate);
    }

    // union candidates that belong to the same <-> domain and bucket them
    // by plane, bottom elevation, material, and condition
    for (const domainAssemblyHash in byDomainAssemblyCondition) {
      const candidates = byDomainAssemblyCondition[domainAssemblyHash];
      if (candidates.length === 0) {
        continue;
      }

      const plane = candidates[0].plane;
      const layers = candidates[0].layers;
      const condition = candidates[0].condition;
      const leftRoomCondition = candidates[0].leftRoomCondition;
      const rightRoomCondition = candidates[0].rightRoomCondition;
      const generatingLevels = new Set<LevelHandle>(candidates.flatMap((c) => Array.from(c.generatingLevels)));

      const assemblyHash = wallStructuralAssemblyName(Object.values(layers), wallCatalog);
      const conditionHash = hashWallCondition(condition, settings);
      const roomConditionPairHash = hashRoomConditionPair(leftRoomCondition, rightRoomCondition);

      const minLevelIndex = Math.min(...candidates.map((c) => c.levelIndex));

      // gather rooms from all candidates
      const leftRooms = new Set<RoomHandle>();
      const rightRooms = new Set<RoomHandle>();
      for (const candidate of candidates) {
        for (const leftRoom of Array.from(candidate.leftRooms)) {
          leftRooms.add(leftRoom);
        }
        for (const rightRoom of Array.from(candidate.rightRooms)) {
          rightRooms.add(rightRoom);
        }
      }

      // union horizontal strips together
      const unionedPolygons = polyOp({
        pls: candidates.map((c) => [c.domain]),
        op: "union",
        roundingMode: PolygonRoundingMode.SNAP_ROUND,
      });

      for (const [polygon] of unionedPolygons) {
        const aabb2 = polyLine2AABB2(polygon);
        const hash = `${conditionHash}_${roomConditionPairHash}_${planeHash}_${assemblyHash}_${aabb2.minX.toFixed(
          2,
        )}_${aabb2.maxX.toFixed(2)}`;

        if (!processed[hash]) {
          processed[hash] = [];
        }
        processed[hash].push({
          domain: polygon,
          plane,
          aabb2,
          layers,
          levelIndex: minLevelIndex,
          generatingLevels,
          condition,
          leftRoomCondition,
          rightRoomCondition,
          leftRooms,
          rightRooms,
          parentHandles: candidates.reduce(
            (acc, c) => {
              Object.assign(acc, c.parentHandles);
              return acc;
            },
            {} as { [id: string]: BuildingEntityHandle | DocEntityHandle },
          ),
        });
      }
    }
  }

  return processed;
}

/**
 * Union candidates that belong to the same plane, bottom elevation, material, and condition
 */
export function candidatesByPlaneAssemblyCondition(
  settings: WallStructuralDomainSettings,
  domainCandidates: DomainCandidates,
  wallCatalog: WallCatalogSettings,
): DomainCandidates {
  const processed: DomainCandidates = {};
  for (const domainHash in domainCandidates) {
    const candidates = domainCandidates[domainHash];

    const plane = candidates[0].plane;
    const layers = candidates[0].layers;
    const condition = candidates[0].condition;
    const leftRoomCondition = candidates[0].leftRoomCondition;
    const rightRoomCondition = candidates[0].rightRoomCondition;
    const generatingLevels = new Set<LevelHandle>(candidates.flatMap((c) => Array.from(c.generatingLevels)));

    const minLevelIndex = Math.min(...candidates.map((c) => c.levelIndex));

    const leftRooms = new Set<RoomHandle>(candidates.flatMap((c) => Array.from(c.leftRooms)));
    const rightRooms = new Set<RoomHandle>(candidates.flatMap((c) => Array.from(c.rightRooms)));

    const planeHash = hashPlane(plane);
    const layersHash = wallStructuralAssemblyName(Object.values(layers), wallCatalog);
    const conditionHash = hashWallCondition(condition, settings);
    const roomConditionPairHash = hashRoomConditionPair(leftRoomCondition, rightRoomCondition);
    const hash = `${conditionHash}_${roomConditionPairHash}_${layersHash}_${planeHash}`;

    const unionedPolygons = polyOp({
      pls: candidates.map((c) => [c.domain]),
      op: "union",
      roundingMode: PolygonRoundingMode.SNAP_ROUND,
    });

    for (const [polygon] of unionedPolygons) {
      if (!processed[hash]) {
        processed[hash] = [];
      }

      processed[hash].push({
        domain: polygon,
        plane,
        aabb2: polyLine2AABB2(polygon),
        layers,
        levelIndex: minLevelIndex,
        generatingLevels,
        condition,
        leftRoomCondition,
        rightRoomCondition,
        leftRooms,
        rightRooms,
        parentHandles: candidates.reduce(
          (acc, c) => {
            Object.assign(acc, c.parentHandles);
            return acc;
          },
          {} as { [id: string]: BuildingEntityHandle | DocEntityHandle },
        ),
      });
    }
  }

  return processed;
}

/**
 * Propagate domain boundaries to all candidates (overrides original plane groups)
 * ```
 *    +----------+    +-+---+----+
 *    |          |    | |   |    |
 *    +-+---+----+ => +-+---+----+
 *    | |   |    |    | |   |    |
 *    +-+---+----+    +-+---+----+
 * ```
 */
export function propagateHorizontalDomains(byPlane: DomainCandidatesByPlane, settings: WallStructuralDomainSettings) {
  const { splitNonRectangularDomains = false } = settings;

  const processed: DomainCandidatesByPlane = {};

  for (const planeHash in byPlane) {
    const { candidates } = byPlane[planeHash];

    const nonRectangularCandidates: DomainCandidate[] = [];
    const rectangularCandidates: DomainCandidate[] = [];

    for (const candidate of candidates) {
      if (polyLine2IsRectangle(candidate.domain)) {
        rectangularCandidates.push(candidate);
      } else {
        nonRectangularCandidates.push(candidate);
      }
    }

    // attempt to split rectangles from non-rectangular candidates
    const postSplitNonRectangularCandidates: DomainCandidate[] = [];
    if (splitNonRectangularDomains) {
      for (const candidate of nonRectangularCandidates) {
        const bottomIndex = polyLine2MinXEdgeIndex(candidate.domain);
        const bottomSegment = polyLine2Edge(candidate.domain, bottomIndex) as unknown as Line2Segment;
        const [bx, by] = line2Vec(bottomSegment);
        if (vec2AngleBetween(bx, by, 1, 0) > SLOPE_EPSILON) {
          // bottom edge is not horizontal
          postSplitNonRectangularCandidates.push(candidate);
          continue;
        }

        const prevEdge = polyLine2Edge(candidate.domain, bottomIndex - 1) as unknown as Line2Segment;
        const [px, py] = line2Vec(prevEdge);
        if (vec2AngleBetween(px, py, 1, 0) > SLOPE_EPSILON) {
          // prev edge is not vertical
          postSplitNonRectangularCandidates.push(candidate);
          continue;
        }

        const nextEdge = polyLine2Edge(candidate.domain, bottomIndex + 1) as unknown as Line2Segment;
        const [nx, ny] = line2Vec(nextEdge);
        if (vec2AngleBetween(nx, ny, 1, 0) > SLOPE_EPSILON) {
          // next edge is not vertical
          postSplitNonRectangularCandidates.push(candidate);
          continue;
        }

        let shortestSegment: Line2Segment | undefined;
        if (line2Len(...nextEdge) < line2Len(...prevEdge)) {
          shortestSegment = nextEdge;
        } else {
          shortestSegment = prevEdge;
        }

        const shortestAABB2 = polyLine2AABB2(shortestSegment);
        const ray = ray2New(shortestAABB2.maxX, 0, 0, 1);

        const splitPolyLine2s = splitPolyLine2ByRay2(candidate.domain, ray);

        if (splitPolyLine2s.length > 1) {
          for (const splitPolyLine2 of splitPolyLine2s) {
            const aabb2 = polyLine2AABB2(splitPolyLine2);
            if (aabb2.minX === shortestAABB2.minX) {
              rectangularCandidates.push({
                ...candidate,
                domain: splitPolyLine2,
              });
            } else {
              postSplitNonRectangularCandidates.push({
                ...candidate,
                domain: splitPolyLine2,
              });
            }
          }
        } else {
          postSplitNonRectangularCandidates.push(candidate);
        }
      }
    } else {
      postSplitNonRectangularCandidates.push(...nonRectangularCandidates);
    }

    // gather domain boundaries
    const domainBoundaries = new Set<number>();
    for (const candidate of rectangularCandidates) {
      const { aabb2 } = candidate;
      domainBoundaries.add(aabb2.minY);
      domainBoundaries.add(aabb2.maxY);
    }

    const rays: Ray2[] = Array.from(domainBoundaries).map((horizontalBreak) => ray2New(0, horizontalBreak, 1, 0));

    const split: DomainCandidate[] = [];
    for (const candidate of rectangularCandidates) {
      const { domain } = candidate;

      const splitPolyLine2s: PolyLine2[] = splitPolyLine2ByRay2s(domain, rays);

      if (splitPolyLine2s.length > 1) {
        for (const splitPolyLine2 of splitPolyLine2s) {
          split.push({
            ...candidate,
            domain: splitPolyLine2,
          });
        }
      } else {
        split.push(candidate);
      }
    }

    processed[planeHash] = {
      ...byPlane[planeHash],
      candidates: [...split, ...postSplitNonRectangularCandidates],
    };
  }

  return processed;
}

export function addCandidatesByPlaneFromWallSystem(
  settings: WallStructuralDomainSettings,
  wallCatalog: WallCatalogSettings,
  wallSystem: WallSystem,
  levelIndexById: LevelIndexByLevelId,
  domainsByPlane: DomainCandidatesByPlane,
  trimFaces: TrimFaces,
  defaultLevelIndex: number,
  generatingLevels: Set<LevelHandle>,
  parentHandles: { [id: string]: BuildingEntityHandle | DocEntityHandle },
) {
  const { walls } = wallSystem;

  for (const wallId in walls) {
    const wall = walls[wallId];
    if (wall.wallType !== WallType.SOLID) {
      continue;
    }

    parentHandles[wall.id] = {
      entityType: "WALL",
      id: wall.id,
    };

    const structuralMassesLOD2 = wallMassLayers(wall.mass, WallLayerType.STRUCTURE, WallLOD.L2);

    for (const mass of structuralMassesLOD2) {
      addCandidatesByPlaneFromWallMass(
        settings,
        wallCatalog,
        mass,
        levelIndexById,
        domainsByPlane,
        trimFaces,
        defaultLevelIndex,
        generatingLevels,
        parentHandles,
      );
    }
  }
}
function addCandidatesByPlaneFromWallMass(
  settings: WallStructuralDomainSettings,
  wallCatalog: WallCatalogSettings,
  mass: WallMass,
  levelIndexById: LevelIndexByLevelId,
  domainsByPlane: DomainCandidatesByPlane,
  trimFaces: TrimFaces,
  defaultLevelIndex: number,
  generatingLevels: Set<LevelHandle>,
  parentHandles: { [id: string]: BuildingEntityHandle | DocEntityHandle },
) {
  const condition = mass.path.wallCondition;
  const leftRoomCondition = roomEnclosureToRoomCondition(mass.path.leftRoom?.attributes.roomEnclosure);
  const rightRoomCondition = roomEnclosureToRoomCondition(mass.path.rightRoom?.attributes.roomEnclosure);

  const domainFace = massDomain(mass);
  if (!domainFace) {
    return;
  }

  let minLevelIndex = defaultLevelIndex;

  const leftRooms = new Set<RoomHandle>();
  const rightRooms = new Set<RoomHandle>();

  if (mass.path.leftRoom && !roomIsVirtualRoofRoom(mass.path.leftRoom)) {
    const { roomHandle, level } = mass.path.leftRoom;
    minLevelIndex = Math.min(minLevelIndex, levelIndexById[level.id]);
    leftRooms.add(roomHandle);
  }
  if (mass.path.rightRoom && !roomIsVirtualRoofRoom(mass.path.rightRoom)) {
    const { roomHandle, level } = mass.path.rightRoom;
    minLevelIndex = Math.min(minLevelIndex, levelIndexById[level.id]);
    rightRooms.add(roomHandle);
  }

  const plane = domainFace.plane;

  const planeConditionLevelHash = `${hashPlane(plane)}_${hashWallCondition(condition, settings)}_L:${minLevelIndex}`;

  if (!domainsByPlane[planeConditionLevelHash]) {
    domainsByPlane[planeConditionLevelHash] = {
      plane: domainFace.plane,
      candidates: [],
    };
  } else {
    // reproject the face into the common plane
    faceProjectOnPlane(domainFace, domainsByPlane[planeConditionLevelHash].plane);
  }

  const byPlaneConditionLevel = domainsByPlane[planeConditionLevelHash];

  const layers: { [hash: string]: WallAssemblyStructuralLayer } = {};
  for (const layer of mass.wallAssembly.structuralLayers) {
    const material = wallCatalog.wallMaterials[layer.wallMaterialId];
    if (!material) {
      continue;
    }
    const layerHash = structuralMaterialHash(material);
    layers[layerHash] = layer;
  }

  // gather trim faces that are at or above the level index
  const atOrAboveTrimFaces = Object.entries(trimFaces.offset).reduce((acc, [trimFacesLevel, levelTrimFaces]) => {
    if (parseInt(trimFacesLevel) >= minLevelIndex) {
      acc.push(...levelTrimFaces);
    }
    return acc;
  }, [] as Face[]);

  const belowTrimFaces = Object.entries(trimFaces.original).reduce((acc, [trimFacesLevel, levelTrimFaces]) => {
    if (parseInt(trimFacesLevel) < minLevelIndex) {
      acc.push(...levelTrimFaces);
    }
    return acc;
  }, [] as Face[]);

  const selectedTrimFaces = [...atOrAboveTrimFaces, ...belowTrimFaces];

  const aabb2 = polyLine2AABB2(domainFace.loops[0]);
  if (selectedTrimFaces.length > 0) {
    const trimmed = trimFaceWithFaces(domainFace, selectedTrimFaces);

    byPlaneConditionLevel.candidates.push(
      ...trimmed.faces.map((f) => ({
        domain: f.loops[0],
        plane: f.plane,
        aabb2,
        levelIndex: minLevelIndex,
        layers,
        condition,
        leftRoomCondition,
        rightRoomCondition,
        leftRooms,
        rightRooms,
        generatingLevels,
        parentHandles,
      })),
    );
  } else {
    byPlaneConditionLevel.candidates.push({
      domain: domainFace.loops[0],
      plane: domainFace.plane,
      aabb2,
      levelIndex: minLevelIndex,
      layers,
      condition,
      leftRoomCondition,
      rightRoomCondition,
      leftRooms,
      rightRooms,
      generatingLevels,
      parentHandles,
    });
  }
}

/**
 * Collect the faces from all GeneratedRoofSystems that should be used
 * to trim wall domains below them.
 */
function getTrimFacesFromRoofSystems(
  roofSystems: Table<GeneratedRoofSystem>,
  levelIndicesById: LevelIndexByLevelId,
  settings: WallStructuralDomainSettings,
): TrimFaces {
  const { trimWithRoofBottomFaces = true, trimWithRoofTopFaces = true, trimFaceOffset = 8 } = settings;

  const original: TrimFacesByLevelIndex = {};
  for (const roofSystemId in roofSystems) {
    const roofSystem = roofSystems[roofSystemId];
    for (const roofId in roofSystem.roofs) {
      const roof = roofSystem.roofs[roofId];
      const levelIndex = levelIndicesById[roof.levelId];
      if (!original[levelIndex]) {
        original[levelIndex] = [];
      }

      if (trimWithRoofBottomFaces) {
        original[levelIndex].push(
          // the bottom faces will either be the bottom of a flat roof or
          // the inner surfaces of a vaulted roof. These will be pointed
          // downwards, so reverse them.
          ...roof.bottomFaces.map((f) => {
            const [, , z] = planeNormal(f.plane);
            if (z < 0) {
              return faceReverse(faceCopy(f));
            }
            return f;
          }),
        );
      }
      if (trimWithRoofTopFaces) {
        // the top faces serve as a backup in case the bottom faces do not
        // clip the walls adequately.
        original[levelIndex].push(...roof.roofTopFaces.map((r) => r.face));
      }
    }
  }

  const offset: TrimFacesByLevelIndex = {};
  if (typeof trimFaceOffset === "number") {
    for (const levelIndex in original) {
      const levelFaces = unionCoplanarFaces(original[levelIndex]);
      offset[levelIndex] = levelFaces.map((f) => {
        /**
         * It is very common for walls to be a little bit longer than the roofs
         * above them. This is a bit of a hack to ensure that the roofs are larger
         * than the walls below them.
         */
        const offset = offsetPolyLine2SSkFallback(f.loops[0], -trimFaceOffset);
        return faceNew([offset], f.plane);
      });
    }
  }

  return { original, offset };
}

function hashWallCondition(condition: WallCondition, settings: WallStructuralDomainSettings) {
  const exteriorInterior = condition.match(/EXTERIOR/) ? "EXT" : "INT";

  if (settings.breakAtGarageCondition) {
    const hasGarage = condition.match(/GARAGE/);
    return `${exteriorInterior}_${hasGarage ? "GARAGE" : "NOGARAGE"}`;
  }

  return exteriorInterior;
}

function roomEnclosureToRoomCondition(roomEnclosure: RoomEnclosure | undefined): RoomCondition {
  if (roomEnclosure === RoomEnclosure.CONDITIONED) {
    return RoomCondition.CONDITIONED;
  }

  return RoomCondition.UNCONDITIONED;
}

function hashRoomConditionPair(leftCondition: RoomCondition, rightCondition: RoomCondition): string {
  return `${leftCondition}_${rightCondition}`;
}

function hashPlane(plane: Plane) {
  const [nx, ny, nz] = planeNormal(plane);
  const angleX = angleClean(vec3AngleBetween(nx, ny, nz, 1, 0, 0));
  const angleY = angleClean(vec3AngleBetween(nx, ny, nz, 0, 1, 0));
  const distance = planeDistanceTo(plane, 0, 0, 0);
  return `${angleX.toFixed(2)}-${angleY.toFixed(2)}-${distance.toFixed(2)}`;
}

/**
 * Build a face to represent a wall's maximum fundamental structural domain - this will get
 * split, trimmed, and unioned with other faces.
 */
function massDomain(mass: WallMass, minFaceArea: number = ZERO_EPSILON): Face | null {
  // build a face from the path
  let xS = mass.path.startX;
  let yS = mass.path.startY;
  let xE = mass.path.endX;
  let yE = mass.path.endY;

  const isWallPlaneMass = wallAssemblyLayerQuery(mass, (layer) => layer.layerSource?.type === "WALL_PLANE_RULE");
  if (isWallPlaneMass) {
    /**
     * @NOTE WALLS V2 - this is a hack to force walls from walls v2 conforms to the expectations of
     * the estimate workbooks, which rely on the legacy wall path domain to compute the span of the assembly
     * mass. this is not the case with walls v2, as the path of the wall does not match
     * up with a room edge, but rather with the domain of the wall plane.
     *
     */
    const { leftDomain, rightDomain } = mass;

    // Left domains are oriented opposite the wall path, so normalize both domains
    // to wall-path direction before building the combined max span.
    const leftPathStartT = Math.min(1 - leftDomain.startT, 1 - leftDomain.endT);
    const leftPathEndT = Math.max(1 - leftDomain.startT, 1 - leftDomain.endT);
    const rightPathStartT = Math.min(rightDomain.startT, rightDomain.endT);
    const rightPathEndT = Math.max(rightDomain.startT, rightDomain.endT);

    const startT = Math.min(leftPathStartT, rightPathStartT);
    const endT = Math.max(leftPathEndT, rightPathEndT);

    const [tempXS, tempYS] = line2Eval(xS, yS, xE, yE, startT, true);
    const [tempXE, tempYE] = line2Eval(xS, yS, xE, yE, endT, true);

    xS = tempXS;
    yS = tempYS;
    xE = tempXE;
    yE = tempYE;
  }

  const zB = mass.floorElevation; // + mass.section.bottomOffset;
  const zT = mass.floorElevation + mass.section.topOffset;

  /**
   * ```
   * 3: xS,yS,zT      2: xE,yE,zT
   *       + ------------- +
   *       |               |
   *       |               |
   *       + ------------- +
   * 0: xS,yS,zB      1: xE,yE,zB
   * ```
   */
  const outline3 = polyLine3New([
    xS,
    yS,
    zB, // 0
    xE,
    yE,
    zB, // 1
    xE,
    yE,
    zT, // 2
    xS,
    yS,
    zT, // 3
    xS,
    yS,
    zB, // 0
  ]);

  // Use fixed Z values (0 and 1) to construct the plane so that all masses
  // from the same wall produce identical Plane objects. Using mass-specific
  // zB/zT causes floating-point differences in the cross product and
  // normalization.
  let plane = planeByThreePoints(xS, yS, 0, xS, yS, 1, xE, yE, 0);
  if (!plane) {
    return null;
  }

  // unify planes so they are are all generally facing the same direction
  const [nx] = planeNormal(plane);
  if (nx < 0) {
    plane = planeCopyReversed(plane);
  }
  const [, ny] = planeNormal(plane);
  if (ny < 0) {
    plane = planeCopyReversed(plane);
  }

  const outline2 = polyLine2SetCW(planeProjectPolyLine3(plane, outline3));
  const face = faceNew([outline2], plane);

  if (faceArea(face) < minFaceArea) {
    return null;
  }

  return face;
}
